/**
 * The hooks a dish row drives its controls from: a debounced search behind the
 * Dish field, the unit list behind the Unit field once a food is picked, the
 * nutrient profile behind the macro columns once a unit and a quantity are
 * both in — and, for a row written out by hand, the whole unit catalog, since
 * there is no food behind it to narrow that list down.
 *
 * One instance of each lives on the row that uses it — `MealOptionCard`
 * builds a fresh set per `DishEntry`, the same way the row owns its own
 * `useState` for anything else about it.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import {
  fetchFoodNutrition,
  fetchFoodUnitCatalog,
  fetchFoodUnits,
  searchFoods,
} from "../services/foodsService";
import type {
  FoodNutritionResult,
  FoodOption,
  FoodUnitOption,
} from "../types/plans.types";

/** Waited out before a keystroke becomes a request - long enough that typing
 *  "chicken" is one call, not seven. */
const SEARCH_DEBOUNCE_MS = 300;

/** Waited out before a quantity change is priced - the same trade as the
 *  search box's, against a heavier call typed into far more often. */
const NUTRITION_DEBOUNCE_MS = 400;

/** Shared so a hook with nothing to offer hands back the same array every
 *  time - a fresh `[]` per render is a new value, and a new value re-renders
 *  every row reading it. */
const NO_UNITS: FoodUnitOption[] = [];

export interface FoodSearchState {
  /** Sets the query and (re)starts the debounce - what the Dish field's
   *  `onSearchChange` calls on every keystroke, and once on open. */
  setQuery: (query: string) => void;
  options: FoodOption[];
  /** True from the moment a query is accepted to the moment its page lands -
   *  covers the debounce wait as well as the request itself, so a list that
   *  is about to change never reads as final. */
  loading: boolean;
  /** Fetches the next page and appends it - the Dish field's list scrolled to
   *  its end. A no-op past the last page, or while one is already in flight. */
  loadMore: () => void;
}

/**
 * Search-as-you-type behind the Dish field.
 *
 * Not cached and not shared between rows: two rows searching "chicken" ask
 * the backend twice, which is the simpler trade against a cache that would
 * have to be invalidated by nothing, ever, since the catalog does not change
 * under an open plan.
 */
export function useFoodSearch(): FoodSearchState {
  const getToken = useTokenGetter();
  const [options, setOptions] = useState<FoodOption[]>([]);
  const [loading, setLoading] = useState(false);

  const queryRef = useRef("");
  const pageRef = useRef(1);
  const hasNextRef = useRef(false);
  // Bumped on every request; a response is only applied if it is still the
  // latest one asked for - what stops a slow page 1 from landing after a
  // faster page 2 and putting the list back a keystroke.
  const requestIdRef = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const run = useCallback(
    (query: string, page: number, append: boolean) => {
      const requestId = (requestIdRef.current += 1);
      setLoading(true);

      searchFoods(getToken, { query, page })
        .then((result) => {
          if (requestId !== requestIdRef.current) return;
          hasNextRef.current = result.hasNext;
          pageRef.current = page;
          setOptions((current) =>
            append ? [...current, ...result.results] : result.results
          );
        })
        .catch(() => {
          if (requestId !== requestIdRef.current) return;
          if (!append) setOptions([]);
        })
        .finally(() => {
          if (requestId === requestIdRef.current) setLoading(false);
        });
    },
    [getToken]
  );

  const setQuery = useCallback(
    (query: string) => {
      queryRef.current = query;
      if (debounceRef.current) clearTimeout(debounceRef.current);

      // A blank query is a browse, not a search - it costs nothing extra to
      // ask for right away, and doing so is what lets the Dish field open on
      // the catalog rather than on nothing.
      if (!query.trim()) {
        run(query, 1, false);
        return;
      }

      debounceRef.current = setTimeout(() => run(query, 1, false), SEARCH_DEBOUNCE_MS);
    },
    [run]
  );

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  const loadMore = useCallback(() => {
    if (!hasNextRef.current || loading) return;
    run(queryRef.current, pageRef.current + 1, true);
  }, [loading, run]);

  return { setQuery, options, loading, loadMore };
}

/** One food's unit list, per food, shared by every row that has ever asked
 *  for it - the units of a given food do not change while a plan is open. */
const unitsCache = new Map<number, FoodUnitOption[]>();

export interface FoodUnitsState {
  options: FoodUnitOption[];
  loading: boolean;
}

/** The Unit field's options, fetched once a food is picked and cached from
 *  then on. Empty and idle while `foodId` is `null` - there is nothing to ask. */
export function useFoodUnits(foodId: number | null): FoodUnitsState {
  const getToken = useTokenGetter();
  const [options, setOptions] = useState<FoodUnitOption[]>(
    () => (foodId != null && unitsCache.get(foodId)) || NO_UNITS
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (foodId == null) {
      // `NO_UNITS` rather than a fresh `[]`: this runs on a render that set
      // no state, and handing back a new array would be a state change, and
      // so another render, and so on without end.
      setOptions(NO_UNITS);
      return;
    }

    const cached = unitsCache.get(foodId);
    if (cached) {
      setOptions(cached);
      return;
    }

    let cancelled = false;
    setLoading(true);

    fetchFoodUnits(getToken, foodId)
      .then((units) => {
        if (cancelled) return;
        unitsCache.set(foodId, units);
        setOptions(units);
      })
      .catch(() => {
        if (!cancelled) setOptions(NO_UNITS);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // `getToken` is deliberately absent - it is stable by construction, and
    // depending on it is what turns this effect into a render loop.
  }, [foodId, getToken]);

  return { options, loading };
}

/**
 * The whole unit list, which is the same list for every row that asks — so it
 * is fetched once per tab and held here. The request itself is shared too: a
 * card of five custom rows mounting together asks the backend once, not five
 * times.
 */
let unitCatalog: FoodUnitOption[] | null = null;
let unitCatalogRequest: Promise<FoodUnitOption[]> | null = null;

/**
 * Every unit the catalog knows, for a row with no food to narrow them down.
 *
 * A failure leaves the list empty rather than reported: the field it fills is
 * `creatable`, so a unit can still be typed, and a custom row is already a row
 * being written by hand.
 */
export function useFoodUnitCatalog(): FoodUnitsState {
  const getToken = useTokenGetter();
  const [options, setOptions] = useState<FoodUnitOption[]>(
    unitCatalog ?? NO_UNITS
  );
  const [loading, setLoading] = useState(!unitCatalog);

  useEffect(() => {
    if (unitCatalog) return;

    let cancelled = false;
    setLoading(true);

    // Whatever request is already in flight, or a new one — the first row to
    // ask starts it and the rest wait on the same promise.
    if (!unitCatalogRequest) {
      unitCatalogRequest = fetchFoodUnitCatalog(getToken);
    }

    unitCatalogRequest
      .then((units) => {
        unitCatalog = units;
        if (!cancelled) setOptions(units);
      })
      .catch(() => {
        // Dropped, so the next row to mount tries again rather than sharing a
        // promise that has already failed.
        unitCatalogRequest = null;
        if (!cancelled) setOptions(NO_UNITS);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // `getToken` is stable by construction — see `useTokenGetter`.
  }, [getToken]);

  return { options, loading };
}

/**
 * Prices a food row: debounced on the quantity the way the search box is
 * debounced on typing, but immediate on a food or unit change, since those
 * are picks rather than keystrokes.
 *
 * Hands the result to `onResolved` rather than returning it, so the row can
 * write it straight onto the `DishEntry` - `dishMacros` then reads it back
 * synchronously, the same way it reads the local catalog for a row that
 * predates this call. `onResolved` is read through a ref, so passing a new
 * function every render (as `(patch) => onUpdate(patch)` naturally is) does
 * not restart the debounce.
 */
export function useFoodNutrition(
  foodId: number | null,
  unitId: number | null,
  quantity: number,
  onResolved: (result: FoodNutritionResult | null) => void
): { loading: boolean } {
  const getToken = useTokenGetter();
  const [loading, setLoading] = useState(false);

  const onResolvedRef = useRef(onResolved);
  useEffect(() => {
    onResolvedRef.current = onResolved;
  }, [onResolved]);

  const requestIdRef = useRef(0);

  useEffect(() => {
    // A half-filled row has nothing to price yet - and nothing here clears
    // what is already on the entry, so a food or unit change stays cleared
    // for exactly as long as the caller that made the change left it.
    if (foodId == null || unitId == null || !quantity) return;

    const requestId = (requestIdRef.current += 1);
    setLoading(true);

    const timer = setTimeout(() => {
      fetchFoodNutrition(getToken, { foodId, unitId, quantity })
        .then((result) => {
          if (requestId === requestIdRef.current) onResolvedRef.current(result);
        })
        .catch(() => {
          if (requestId === requestIdRef.current) onResolvedRef.current(null);
        })
        .finally(() => {
          if (requestId === requestIdRef.current) setLoading(false);
        });
    }, NUTRITION_DEBOUNCE_MS);

    return () => clearTimeout(timer);
    // `getToken` is stable by construction - see `useTokenGetter`. Depending
    // on Clerk's own would clear and restart this timer on every render, so
    // the debounce would never elapse and the row would never be priced.
  }, [foodId, unitId, quantity, getToken]);

  return { loading };
}
